import { ClientFeatureState } from './ClientFeatureStateType';

export interface ICreateClientFeatureInput {
  name: string;
  state?: ClientFeatureState;
}

export interface ISaveClientFeatureInput {
  id: number;
  name: string;
  state: ClientFeatureState;
}

export interface IClientFeatureChanges {
  name?: string;
  description?: string;
  state?: ClientFeatureState;
}

export enum ClientFeatureLogAction {
  CREATED = 'created',
  UPDATED = 'updated',
  DELETED = 'deleted',
}

export enum ClientFeature {
  ADD_ASPIRE_SHIPPING_LINE = 'addAspireShippingLine',
  ADD_BILLING_ADDRESS = 'addBillingAddress',
  ALL_PEOPLE_SEGMENT = 'all_people_segment',
  ALLOW_GMAIL_OUTLOOK_TOGETHER = 'allowGmailOutlookTogether',
  ALLOY_INTEGRATIONS = 'alloyIntegrations',
  APPLICANT_REVIEW = 'applicantReview',
  AUTOMATED_APPROVAL_CPS = 'automatedApprovalCPS',
  BASIC_TERMS = 'basicTerms',
  BULK_TERMS = 'bulkTerms',
  BUDGET_ALLOCATION = 'budgetAllocation',
  BYPASS_SHOPIFY_INVENTORY = 'bypassShopifyInventory',
  CLIENT_IDLE_TIMEOUT = 'clientIdleTimeout',

  CREATOR_SEARCH_FUZZY_SEARCH = 'creatorSearchFuzzySearch',
  DEMO = 'demo',
  DEMO_ACCOUNT_MODE = 'DemoAccountMode',
  DISABLE_IMAGE_SEARCH = 'disableImageSearch',
  DISCOVERY_KEYWORD_SEARCH = 'discoveryKeywordSearch',
  DISCOVERY_SIMILAR_CREATORS = 'discoverySimilarCreators',
  DISCOVERY_TIKTOK = 'DiscoveryTiktok',
  EMAIL_SUBADDRESSING = 'EmailSubaddressing',
  EMAIL_TEMPLATE = 'emailTemplate',
  EMBEDDED_REPORTING = 'embeddedReporting',
  /**
   * Gates Superset-backed embedded reports. A client must ALSO have
   * EMBEDDED_REPORTING enabled to see any embedded reporting at all; this flag
   * additionally reveals provider=superset items (dark rollout). Must stay in
   * sync with the frontend `ClientFeature.EMBEDDED_SUPERSET` value.
   */
  EMBEDDED_SUPERSET = 'embeddedSuperset',
  EMV = 'emv',
  EMV_CURRENCY = 'emvCurrency',
  ENABLE_MULTIPLE_SHOPIFY = 'enableMultipleShopify',
  ENABLE_VANITY_LINKS = 'enableVanityLinks',
  ENABLE_REQUIREMENTS = 'enableRequirements',
  ENABLE_TMV = 'enableTmv',
  EVENT_TRACKING_DISABLED = 'eventTrackingDisabled',
  GCR_SUBMISSION_HISTORY_ENABLED = 'gcrSubmission',
  KLAVIYO_ADVANCED_SETTINGS = 'klaviyoAdvancedSettings',
  GOOGLE_DRIVE_LIMITS = 'googleDriveLimits',
  GROUP_CONTENT_REVIEW = 'groupContentReview',
  HIGH_FREQUENCY_INDEXING = 'highFrequencyIndexing',
  HIDE_CREATOR_SEARCH = 'hideCreatorSearch',
  HIDE_IG_AUD_AUTH_ELIGIBILITY_RESTRICTION = 'hideIgAudAuthEligibilityRestriction',
  IGDM_ADOPTION_BANNER = 'igdmAdoptionBanner',
  // EPD-6792: source the Impact dashboard's pillar %-change from flex `reporting.impact.*.percentDiff`
  // (legacy "Your Impact" parity) instead of the scf-reconstructed delta. Values stay scf/EMV; only
  // the delta basis flips to flex. Default off. Reversible pending gold prior-period reconciliation.
  IMPACT_DASHBOARD_FLEX_DELTAS = 'ImpactDashboardFlexDeltas',
  IG_PARTNERSHIP_ADS = 'IgPartnershipAds',
  INSTAGRAM_BRANDED_CONTENT_TAGGING_V2 = 'InstagramBrandedContentTaggingV2',
  INSTAGRAM_CREATOR_SEACH_V2 = 'instagramCreatorSearchV2',
  INSTAGRAM_PARTNERSHIP_ADS = 'InstagramPartnershipAds',
  // EPD-11741: request Meta's instagram_creator_marketplace_messaging scope in the IGDM connect
  // OAuth. Read by name in backend_server too; keep the string in sync.
  INSTAGRAM_CREATOR_MARKETPLACE_MESSAGING = 'instagramCreatorMarketplaceMessaging',
  LIMITED_SHOPIFY_READ_CUSTOMERS = 'limitShopifyScope',
  NAVIGATION_V1_CHANGES = 'NavigationV1changes',
  /** TEMP EPD-7833 rollout flag — see app clientFeatures.ts; remove after rollout. */
  PPA_ADS_CONTACT_HUB = 'PpaAdsContactHub',
  META_BRANDED_CONTENT = 'MetaBrandedContent',
  MULTI_META_PPA_ACCOUNTS = 'multiMetaPpaAccounts',
  // EPD-8821: gates selecting MULTIPLE brand IG accounts for Paid Partnership
  // Ads / Branded Content on a brief (layers on top of MULTI_META_PPA_ACCOUNTS).
  SELECT_MULTI_META_PPA = 'selectMultiMetaPpa',
  MULTI_TTO_ACCOUNTS = 'multiTtoAccounts',
  OFFLINE_CREATOR_PRODUCT_SELECTION = 'OfflineCreatorProductSelection',
  ON_DEMAND_SOCIAL_ANALYTICS_REFRESH = 'onDemandSocialAnalyticsRefresh',
  PREPOPULATE_APPLICATION_FORM = 'PrepopulateApplicationForm',
  PROJECT_DASHBOARD_ACCEPTED_TERMS = 'projectDashboardAcceptedTerms',
  PROJECT_DASHBOARD_TERMS_CONTENT = 'projectDashboardTermsContent',
  PROJECT_PFA_COLUMNS = 'projectPFAColumns',
  SHOPIFY_EXPIRING_TOKENS = 'shopifyExpiringTokens',
  /**
   * EPD-11269: the cohort runner's pause switch. Read globally (its state, not per client) before every
   * store; anything but `enabled` stops new exchanges. Seeded `disabled`.
   */
  SHOPIFY_TOKEN_MIGRATION_RUNNER = 'shopifyTokenMigrationRunner',
  SHOPIFY_GDPR_DELETE = 'shopifyGDPRDelete',
  /** EPD-10865: lets a client connect a 2nd Shopify store and assign Gifting / Promo & Link roles. */
  SHOPIFY_MULTI_STORE = 'shopifyMultiStore',
  SHOPLESS_SHOPIFY_CONNECTION_ENABLED = 'shoplessShopifyConnectionEnabled',
  SHOW_INSTAGRAM_META = 'showInstagramMeta',
  SHOW_ONBOARDING = 'showOnboarding',
  SHOW_ONBOARDING_FOR_TESTING = 'showOnboardingForTesting',
  SOCIAL_ANALYTICS_ALL_TIME_DEFAULT = 'SocialAnalyticsAllTimeDefault',
  SOCIAL_ANALYTICS_COST_METRICS = 'socialAnalyticsCostMetrics',
  SOCIAL_REPORTS_V2 = 'socialReportsV2',
  TIKTOK_CONTENT_DISCLOSURES = 'TTContentDisclosures',
  /** Commerce → TikTok Shop: the real module instead of the early-access upsell. */
  TIKTOK_SHOP_M1 = 'tiktokShopM1',
  /** Commerce → TikTok Shop → Analytics tab (EPD-12643); also gates the analytics query server-side. */
  TIKTOK_ANALYTICS = 'tiktokAnalytics',
  TIKTOK_CREATOR_SEARCH_V2 = 'tiktokCreatorSearchV2',
  TIKTOK_SPARK_ADS_RECOMMENDATIONS = 'TikTokSparkAdsRecommendations',
  TOP_NAVIGATION = 'topNavigation',
  TRAY_INTEGRATION = 'trayIntegration',
  UCE_INFLUENCER_WHITELISTING = 'UCEInfluencerWhitelisting',
  UPSELL_GATES = 'upsellGates',

  YOUTUBE_CREATOR_SEARCH_V2 = 'youtubeCreatorSearchV2',
  PFA_V2 = 'pfaV2',
  PARSE_MEMBER_SEARCH_QUERY = 'parseMemberSearchQuery',
  TEST_SHAREDRESOURCE_FOR_QA_LOGIN = 'testSharedResourceForQALogin',
  EDIT_UTM_FIELDS_ENABLED = 'editUtmFieldsEnabled',
  SOCIAL_ANALYTICS_SOURCE_FILTER = 'socialAnalyticsSourceFilter',
  SOCIAL_ANALYTICS_MANUAL_ASSINGMENT_FILTER = 'socialAnalyticsManualAssignmentFilter',
  ALLOW_MULTIPLE_CONVERSIONS = 'allowMultipleConversions',
  CONTENT_LIBRARY_LEGACY_SEARCH = 'contentLibraryLegacySearch',
  THIRD_PARTY_TRACKING_URL = 'thirdPartyTrackingUrl',
  BUDGET_DASHBOARD = 'budgetDashboard',
  // EPD-9791 — per-client rollout gate for promo-code auto-resolve (dedupe).
  PROMO_CODE_DEDUPE = 'promoCodeDedupe',
  COUNTER_SIGN_BRIEF = 'counterSignBrief',
  ADVANCE_DISCOUNTING = 'advanceDiscounting',
  PROJECT_V2 = 'projectV2',
  GCR_HIDE_APPROVAL_ACTION = 'gcrHideApprovalAction',
  DOUBLE_PROJECT_MEMBER_SIZE = 'doubleProjectMemberSize',
  OFFER_BOOST = 'offerBoost',
  CUSTOM_STAGES = 'customStages',
  SOCIAL_DATA_RECONCILIATION = 'socialData_reconciliation',
  NON_SHOPIFY_PFA = 'nonShopifyPfa',
  CONVERSION_SETUP_OBJECTIVES = 'conversionSetupObjectives',
  PROJECT_DASHBOARD_V2 = 'projectDashboardV2',
  SALES_OBJECTIVE_FOR_AD_CAMPAIGNS = 'salesObjectiveForAdCampaigns',
  RECRUITMENT_V2 = 'recruitmentV2',
  CASHBACK_PAYMENTS = 'cashbackPayments',
  PFA_BOOST = 'pfaBoost',
  ADVANCED_SELECTION = 'advancedSelectionEligibility',
  AUTOMATION_V2 = 'automationV2',
  INBOX_SENDER_FOLDER = 'inboxSenderFolder',
  SHOW_CREATOR_SORT = 'showCreatorSort',
  WORKFLOW_AUTOMATION = 'workflowAutomation',
  REFRESH_CREATOR_PORTAL = 'refreshCreatorPortal',
  REMOVE_CONTRACT_SIGNER = 'removeContractSigner',
  OFFER_EMAIL_AUTOMATION_M2 = 'offerEmailAutomationM2',
  CONTENT_HUB_FILTER_BAR = 'contentHubFilterBar',
  CONTENT_LIBRARY_FULL_WIDTH_OVERVIEW_MODAL = 'contentLibraryFullWidthOverviewModal',
  STANDARD_BRIEF_MIGRATION = 'standardBriefMigration',
  MEMBER_HUB_ADV_CALCULATIONS = 'MemberHubAdvCalculations',
  MMM_MEDIA_BUDGET = 'mmmMediaBudget',
  NEW_STA_DASH_REVAMP = 'newStaDashRevamp',
  MULTIPLE_BRIEF_TEMPLATE = 'multipleBriefTemplate',
  GCR_NOTIFICATION = 'GcrNotification',
  BUDGET_V2 = 'budgetV2',
  SEND_IG_ADS_AUTH_REQUEST_ACTION = 'sendIgAdsAuthRequestAction',
  CATALOG_INVENTORY_MANAGEMENT = 'catalogInventoryManagement',
  CATALOG_PRODUCT_SELECTION_RULE = 'catalogProductSelectionRule',
  HIDE_REPORTING_CONTENT_TAB = 'hideReportingContentTab',
  REWARD_V2 = 'rewardV2',
  /**
   * EPD-10354 — gates the ui-studio TipTap email composer in Settings → Messages
   * (vs the legacy Draft.js editor), nested under UX_REVAMP. Must be registered
   * here too: the app-api `isClientFeatureEnabled` resolver rejects any name not
   * in this enum, so a frontend-only flag always resolves false.
   */
  EMAIL_EDITOR_UX_REVAMP = 'emailEditorUxRevamp',

  UX_REVAMP = 'uxRevamp',
  GUEST_VIEWER_SHARING = 'guestViewerSharing',
  /**
   * EPD-11555 — gates the Share button on the studio Reporting shell and the
   * guest-viewable Reporting route (Impact + Social Summary only). Registered
   * here because the app-api `isClientFeatureEnabled` resolver rejects any name
   * not in this enum, so a frontend-only flag always resolves false. Mirrors the
   * frontend `ClientFeature.REPORTING_SHARING` ('reportingSharing'). Off by
   * default; enable the `client_feature` row per client to roll out.
   */
  REPORTING_SHARING = 'reportingSharing',
  /**
   * EPD-6027 — gates the Content Hub Analytics tab (and the content-detail
   * Compare / over-time / engagement-breakdown surfaces). Mirrors the frontend
   * `ClientFeature.CONTENT_ANALYTICS_UX_REVAMP`. Must be registered here so the
   * feature resolves through `isClientFeatureEnabled` and seeds in non-local envs.
   */
  CONTENT_ANALYTICS_UX_REVAMP = 'contentAnalyticsUxRevamp',
  /**
   * EPD-10192 — Content Hub Analytics "Export Report" button + drawer (CSV, PDF, PowerPoint). Must be
   * registered here so the app-api client-feature resolver returns the real DB
   * value instead of false. Mirrors the frontend `ClientFeature.CONTENT_HUB_EXPORT_REPORT`.
   */
  CONTENT_HUB_EXPORT_REPORT = 'contentHubExportReport',
  /**
   * EPD-9625 — Content Hub multiple-groups-per-content. Must be registered here
   * so the app-api client-feature resolver returns the real DB value instead of
   * false. Mirrors the frontend `ClientFeature.CONTENT_MULTI_GROUP`.
   */
  CONTENT_MULTI_GROUP = 'contentMultiGroup',
  CAMPAIGN_REVAMP = 'campaignRevamp',
  PUBLIC_API = 'publicApi',
  YOUTUBE_BRAND_CONNECT = 'youtubeBrandConnect',
  // EPD-3760: single epic-wide flag gating Content Auto-Assignment, including the
  // tracked-hashtags UI (consolidated 2026-06-09 from trackedHashtags /
  // hashtag_config_ingest / hashtag_assignment_active / hashtag_tier1_active).
  // Value is snake_case (not the usual camelCase) because it must match the
  // backend client-feature registration key exactly.
  // EPD-10570 — GA: registry state was flipped from LIMITED to ENABLED
  // (2026-08-05) so the feature defaults on for all clients; opt-out is via
  // disabledFeatureIds, and any client's prior explicit opt-out is preserved
  // (verified: evaluateFeatureState's ENABLED branch checks disabledFeatureIds
  // regardless of featureIds). The self-serve beta card and its
  // CONTENT_AUTO_ASSIGNMENT_BETA visibility gate were retired here.
  CONTENT_AUTO_ASSIGNMENT = 'content_auto_assignment',
  /**
   * EPD-7817 — per-tier sub-gate for the Tier-3 creator-activity signal, under
   * CONTENT_AUTO_ASSIGNMENT. That master flag went GA above, so it can neither
   * stage this rollout nor switch the signal off without taking Tier-1 and
   * Tier-2 down with it. Off by default; enable per-client to roll out.
   *
   * Gates BOTH sides: backend_server skips the per-member signal (Tier-3 falls
   * back to the per-program proxy, byte-for-byte as before), and
   * AppApiService.programsForMembers skips the ProgramActivity aggregate query
   * entirely — that endpoint also serves callers which never read the field, so
   * without this gate every client would pay for a query they cannot use.
   */
  CONTENT_AUTO_ASSIGNMENT_CREATOR_ACTIVITY_TIER = 'content_auto_assignment_creator_activity_tier',
  /**
   * EPD-11372 — sub-gate broadening CONTENT_AUTO_ASSIGNMENT to assign content to
   * EVERY group its creator belongs to (union + add-merge) instead of only when
   * the creator is in exactly one. Evaluated together with CONTENT_MULTI_GROUP
   * (both must be on) since the broadened write assumes multi-group-capable
   * content. Off by default. UserService.isClientFeatureEnabled resolves false
   * for any name with no `client_feature` DB row — this enum entry is only the
   * type/convention registration; rollout requires creating that row via
   * createClientFeature (ClientFeatureResolver.ts) per client.
   */
  CONTENT_AUTO_ASSIGNMENT_MULTI_GROUP = 'content_auto_assignment_multi_group',
  /**
   * EPD-7319 — gates the affiliate recruiting tab + sub-pages (Landing Page,
   * Automations, Applicants) on the offer-listing page. Backed by the new
   * STA-side affiliate_application_default + affiliate_application_submissions
   * tables + GraphQL surface. Off by default; enable per-client to roll out.
   *
   * Value is `affiliateRecruit` — NOT `affiliateRecruiting`. It must match the
   * registered `client_feature.name` row exactly, and the frontend twin in
   * `app/src/app/constants/clientFeatures.ts` (which gates the live Recruit
   * tab). EPD-10187: this constant read `'affiliateRecruiting'` until it gained
   * its first runtime consumer (the drop-off-capture gate) and was caught
   * against the real table — `getClientFeatureByName` found no row, so
   * `isClientFeatureEnabled` returned false for EVERY client and the feature
   * would have been permanently dark in every environment.
   */
  AFFILIATE_RECRUITING = 'affiliateRecruit',
  /**
   * EPD-6213 — gates the Recruit Hub's Analytics tab. Holds the tab back until every widget under the epic has its data. Off by
   * default; enable per-client to roll out. Must stay in sync with the frontend
   * `ClientFeature.RECRUIT_ANALYTICS` value.
   */
  RECRUIT_ANALYTICS = 'recruitAnalytics',
  /**
   * EPD-8965 / EPD-9087 — gates the Recruit Hub Applicants tab (global
   * applications management across campaigns and offers). Off by default.
   * Must stay in sync with the frontend `ClientFeature.RECRUIT_APPLICANTS`
   * value.
   */
  RECRUIT_APPLICANTS = 'recruitApplicants',
  /**
   * EPD-6792 — gates the Reporting revamp: the primary-nav Reporting item links
   * straight to the new Reporting shell (`/impact_dashboard`) instead of opening
   * the legacy reporting flyout. Off by default. Must stay in sync with the
   * frontend `ClientFeature.REPORTING_REVAMP` value.
   */
  REPORTING_REVAMP = 'reportingRevamp',
  /**
   * EPD-9098 — gates the EPD-8896 adaptive-HLS video path in the GCR viewer.
   * Off by default; GCR plays the raw original at full resolution. Enable
   * per-client to opt into bandwidth-optimized adaptive streaming. Must stay
   * in sync with the frontend `ClientFeature.GCR_ADAPTIVE_VIDEO` value.
   */
  GCR_ADAPTIVE_VIDEO = 'gcrAdaptiveVideo',
  /**
   * EPD-9207 — serve GCS media through the Media CDN host. Off by default;
   * per-client canary. Must stay in sync with the frontend
   * `ClientFeature.MEDIA_CDN` value ('mediaCdn').
   */
  MEDIA_CDN = 'mediaCdn',
  REPORTING_PAYMENT_TAB = 'reportingPaymentTab',
  REPORTING_BUDGET_TAB = 'reportingBudgetTab',
  /** Gates the ACH radio in the studio Send Payment drawer. Must stay
   *  in sync with the frontend twin at
   *  `app/src/app/constants/clientFeatures.ts` — `ACH_PAYMENT_METHOD_SUPPORT`.
   *  `isClientFeatureEnabled` returns false for any name not in this enum. */
  ACH_PAYMENT_METHOD_SUPPORT = 'ACHPaymentMethodSupport',
  /**
   * EPD-11076 — TEMPORARY rollout flag for canarying the corrected PFA
   * reporting ClickHouse pipeline in social-public-api. Off by default;
   * per-client canary. Remove this entry once all clients are cut over to
   * the v2 pipeline. Must stay in sync with the frontend
   * `ClientFeature.PFA_REPORTING_GOLD_V2` value ('pfaReportingGoldV2').
   */
  PFA_REPORTING_GOLD_V2 = 'pfaReportingGoldV2',
  /**
   * EPD-9019 — epic-wide flag for the "Reporting Workflow M1" initiative's first
   * surface, the Content Hub "Send to Ads Manager" flow (EPD-10220 modal). Off
   * by default; enable per-client to roll out. This enum is type-safety/convention
   * only — `isClientFeatureEnabled` resolves against the `client_feature` DB row
   * by name, not this list — so a row named 'reportingWorkflowM1' still has to
   * exist (and be enabled) in each environment before this flag does anything.
   * Must stay in sync with the frontend `ClientFeature.REPORTING_WORKFLOW_M1`
   * value ('reportingWorkflowM1').
   */
  REPORTING_WORKFLOW_M1 = 'reportingWorkflowM1',
  /**
   * EPD-6028 — Content Hub "Social Listening" tab. Off by default; a
   * `client_feature` row named 'contentHubSocialListening' has to exist (and be
   * enabled) in each environment. Must stay in sync with the frontend
   * `ClientFeature.CONTENT_HUB_SOCIAL_LISTENING` value ('contentHubSocialListening').
   */
  CONTENT_HUB_SOCIAL_LISTENING = 'contentHubSocialListening',
  /**
   * EPD-12154 / EPD-12521 — campaign stage table: Posts column, Posted filter and the
   * "Mark as complete" rename of Skip this Stage. Off by default; a `client_feature` row
   * named 'campaignPostsTracking' has to exist (and be enabled) in each environment. Must
   * stay in sync with the frontend `ClientFeature.CAMPAIGN_POSTS_TRACKING` value.
   */
  CAMPAIGN_POSTS_TRACKING = 'campaignPostsTracking',

  /**
   * EPD-12576 — Contact merge. Gates the admin-only merge preview/mutation. Seeded
   * `limited` (off), opted in per client while the feature is internal. Must stay in
   * sync with the frontend `ClientFeature.CONTACT_MERGE` value ('contactMerge').
   */
  CONTACT_MERGE = 'contactMerge',
  // EPD-12762 — Reward Approvals: shows the settings toggle and lets STA email brand users about rewards
  // waiting for approval. Must stay in sync with the frontend `ClientFeature.REWARD_NOTIFICATION`.
  REWARD_NOTIFICATION = 'rewardNotification',
}

export enum ClientFFState {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
}

export interface IClientFFStateInput {
  id: number;
  state: ClientFFState;
}

export interface IClientFFUpdateInput {
  id: string;
  featuresUpdate: IClientFFStateInput[];
}
